import React from "react";
import {
    Box,
    Button,
    HStack,
    Icon,
    IconButton,
    Input,
    Text,
    Tooltip,
    VStack,
} from "@chakra-ui/react";
import { ArrowUpDown, ChevronDown, ChevronUp, FileAudio, RefreshCw, Trash2 } from "lucide-react";

export interface AudioTrackDraft {
    id: string;
    file: File;
    title: string;
}

export interface AudioTracksDraftListProps {
    tracks: AudioTrackDraft[];
    onChange: (tracks: AudioTrackDraft[]) => void;
    isDisabled?: boolean;
}

const formatSize = (bytes: number): string => {
    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(1)} КБ`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
};

export const AudioTracksDraftList: React.FC<AudioTracksDraftListProps> = ({
    tracks,
    onChange,
    isDisabled = false,
}) => {
    const handleMove = (index: number, direction: "up" | "down") => {
        const targetIndex = direction === "up" ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= tracks.length) return;

        const next = [...tracks];
        const [movedItem] = next.splice(index, 1);
        next.splice(targetIndex, 0, movedItem);
        onChange(next);
    };

    const handleTitleChange = (index: number, newTitle: string) => {
        const next = [...tracks];
        next[index] = { ...next[index], title: newTitle };
        onChange(next);
    };

    const handleRemove = (index: number) => {
        onChange(tracks.filter((_, i) => i !== index));
    };

    const handleSortByName = () => {
        const sorted = [...tracks].sort((a, b) =>
            a.file.name.localeCompare(b.file.name, undefined, {
                numeric: true,
                sensitivity: "base",
            })
        );
        onChange(sorted);
    };

    const handleRenumber = () => {
        const renumbered = tracks.map((track, i) => ({
            ...track,
            title: `Частина ${i + 1}`,
        }));
        onChange(renumbered);
    };

    return (
        <VStack align="stretch" spacing={3}>
            {/* Action buttons header */}
            <HStack justify="space-between" align="center" wrap="wrap" gap={2}>
                <Text fontWeight="bold" fontSize="sm">
                    Нові доріжки ({tracks.length}):
                </Text>
                {tracks.length > 1 && (
                    <HStack spacing={2} wrap="wrap">
                        <Button
                            size="xs"
                            variant="ghost"
                            colorScheme="orange"
                            leftIcon={<Icon as={ArrowUpDown} boxSize={3} />}
                            onClick={handleSortByName}
                            isDisabled={isDisabled}
                        >
                            Сортувати A-Z
                        </Button>
                        <Button
                            size="xs"
                            variant="ghost"
                            leftIcon={<Icon as={RefreshCw} boxSize={3} />}
                            onClick={handleRenumber}
                            isDisabled={isDisabled}
                        >
                            Перенумерувати «Частина N»
                        </Button>
                    </HStack>
                )}
            </HStack>

            {/* List of track cards */}
            <VStack align="stretch" spacing={2.5}>
                {tracks.map((track, i) => (
                    <Box
                        key={track.id}
                        p={3}
                        bg="white"
                        borderRadius="md"
                        border="1px solid"
                        borderColor="blackAlpha.200"
                        boxShadow="xs"
                    >
                        <HStack spacing={3} align="center">
                            {/* Sequential order number */}
                            <Text
                                fontSize="sm"
                                fontWeight="bold"
                                color="gray.500"
                                w="22px"
                                textAlign="center"
                                flexShrink={0}
                            >
                                {i + 1}.
                            </Text>

                            {/* Middle: Title input + file details underneath */}
                            <VStack align="stretch" flex={1} spacing={1} minW={0}>
                                <Input
                                    size="sm"
                                    bg="white"
                                    value={track.title}
                                    placeholder="Назва доріжки"
                                    onChange={(e) => handleTitleChange(i, e.target.value)}
                                    isDisabled={isDisabled}
                                />
                                <HStack spacing={1.5} color="gray.500" fontSize="xs" minW={0}>
                                    <Icon as={FileAudio} boxSize={3} flexShrink={0} color="orange.400" />
                                    <Text noOfLines={1} title={track.file.name} wordBreak="break-all" flex={1}>
                                        {track.file.name}
                                    </Text>
                                    <Text color="gray.400" flexShrink={0}>
                                        • {formatSize(track.file.size)}
                                    </Text>
                                </HStack>
                            </VStack>

                            {/* Controls: Move Up, Move Down, Delete */}
                            <HStack spacing={1} flexShrink={0}>
                                <Tooltip label="Перемістити вище" placement="top" openDelay={300}>
                                    <IconButton
                                        aria-label="Перемістити вище"
                                        size="sm"
                                        variant="ghost"
                                        icon={<Icon as={ChevronUp} boxSize={4} />}
                                        isDisabled={isDisabled || i === 0}
                                        onClick={() => handleMove(i, "up")}
                                    />
                                </Tooltip>
                                <Tooltip label="Перемістити нижче" placement="top" openDelay={300}>
                                    <IconButton
                                        aria-label="Перемістити нижче"
                                        size="sm"
                                        variant="ghost"
                                        icon={<Icon as={ChevronDown} boxSize={4} />}
                                        isDisabled={isDisabled || i === tracks.length - 1}
                                        onClick={() => handleMove(i, "down")}
                                    />
                                </Tooltip>
                                <Tooltip label="Вилучити файл" placement="top" openDelay={300}>
                                    <IconButton
                                        aria-label="Вилучити файл"
                                        size="sm"
                                        variant="ghost"
                                        colorScheme="red"
                                        icon={<Icon as={Trash2} boxSize={4} />}
                                        isDisabled={isDisabled}
                                        onClick={() => handleRemove(i)}
                                    />
                                </Tooltip>
                            </HStack>
                        </HStack>
                    </Box>
                ))}
            </VStack>
        </VStack>
    );
};
